import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaInfoCircle, FaCode, FaReact, FaJs, FaNode, FaHtml5, FaCss3Alt, FaBootstrap, FaPython } from 'react-icons/fa';
import { SiMongodb, SiExpress, SiNextdotjs, SiTailwindcss, SiRedux, SiNestjs, SiVite } from 'react-icons/si';
import project1Image from '../project1.png';
import project2Image from '../project2.png';
import project3Image from '../project3.png';
import spexImage from '../spex.png';
import signinSignupImage from '../signin_signup.png';
import codifyDetail1 from '../codify1.png';
import codifyDetail2 from '../codify3.png';
import codifyImage from '../codify10.png';
// Daily Collection Management System detail images
import dailyDetail1 from '../daily-collection-1.png'; // Dashboard view
import dailyDetail2 from '../daily-collection-2.png'; // Add Customer form
import dailyDetail3 from '../daily-collection-3.png'; // Create loan page
import dailyDetail4 from '../daily-collection-4.png'; // Payments section
import dailyDetail5 from '../daily-collection-5.png'; // Holidays management
import dailyDetail6 from '../daily-collection-6.png'; // Help & Video Tutorial
// SPEX ID Topup Random Sweep Giveaway detail images
import sweepDetail1 from '../sweep-1.png'; // Login modal
import sweepDetail2 from '../sweep-2.png'; // CSV upload and parsing
import sweepDetail3 from '../sweep-3.png'; // Game IDs display
import sweepDetail4 from '../sweep-4.png'; // Winners selection
import sweepDetail5 from '../sweep-5.png'; // Download winners
import './Projects.css';

const Projects = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [repoStatus, setRepoStatus] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showTechModal, setShowTechModal] = useState(false);
  const [projectTechnologies, setProjectTechnologies] = useState([]);
  const [clickedHandoverProjects, setClickedHandoverProjects] = useState(new Set());
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  // Technology icon mapping
  const techIcons = {
    'React': { icon: <FaReact />, color: '#61DAFB' },
    'JavaScript': { icon: <FaJs />, color: '#F7DF1E' },
    'Node.js': { icon: <FaNode />, color: '#339933' },
    'MongoDB': { icon: <SiMongodb />, color: '#47A248' },
    'Express': { icon: <SiExpress />, color: '#000000' },
    'Express.js': { icon: <SiExpress />, color: '#000000' },
    'HTML5': { icon: <FaHtml5 />, color: '#E34F26' },
    'CSS3': { icon: <FaCss3Alt />, color: '#1572B6' },
    'Bootstrap': { icon: <FaBootstrap />, color: '#7952B3' },
    'Next.js': { icon: <SiNextdotjs />, color: '#000000' },
    'Nest.js': { icon: <SiNestjs />, color: '#E0234E' },
    'Tailwind CSS': { icon: <SiTailwindcss />, color: '#06B6D4' },
    'Redux': { icon: <SiRedux />, color: '#764ABC' },
    'CSV Parser': { icon: <FaCode />, color: '#FFA500' },
    'Mongoose': { icon: <SiMongodb />, color: '#880000' },
    'Python': { icon: <FaPython />, color: '#3776AB' },
    'yt-dlp': { icon: <FaCode />, color: '#FF6B6B' },
    'pytube': { icon: <FaCode />, color: '#FF6B6B' },
    'Vite': { icon: <SiVite />, color: '#646CFF' }
  };

  const projects = [
    {
      id: 1,
      title: 'Daily Collection Management System',
      description: 'A web-based internal management system designed to record daily collections, transactions, and reports digitally. This system replaces manual record-keeping and enables quick access to historical data through a user-friendly dashboard.',
      image: project1Image,
      technologies: ['React', 'Node.js', 'MongoDB', 'Express'],
      category: 'all',
      github: 'https://github.com/RNAGIT/RDAththidiya',
      githubPrivate: false,
      live: 'https://rd-aththidiya.vercel.app/',
      handOverProject: true,
      featured: true,
      details: [
        {
          image: dailyDetail1,
          description: 'Dashboard view showing Mr. Rashith Abeywickrama\'s Portal with key performance indicators including completion rate, collection coverage, and portfolio summary with total customers and loans.'
        },
        {
          image: dailyDetail2,
          description: 'Add Customer form interface for registering new customers with contact details, NIC number, phone number, email address, and supporting references.'
        },
        {
          image: dailyDetail3,
          description: 'Create loan page with automated loan setup including customer selection, principal amount, interest rate, term, and automatic repayment schedule calculation.'
        },
        {
          image: dailyDetail4,
          description: 'Payments section featuring record payment functionality with loan search, payment summary showing must collect amount, collected amount, and arrears collection totals.'
        },
        {
          image: dailyDetail5,
          description: 'Holidays management interface allowing administrators to add and manage holidays that automatically affect loan payment schedules and calculations.'
        },
        {
          image: dailyDetail6,
          description: 'Help & Video Tutorial page providing comprehensive system documentation and YouTube video guide for learning all features of the Daily Collection system.'
        }
      ]
    },
    {
      id: 2,
      title: 'SPEX ID Topup Random Sweep Giveaway',
      description: 'A giveaway management system that allows users to upload CSV files, select random winners, and manage sweepstakes efficiently. Features a modern dark-themed interface with intuitive controls for parsing participant data.',
      image: project2Image,
      technologies: ['React', 'JavaScript', 'CSV Parser', 'Node.js'],
      category: 'all',
      github: 'https://github.com/RNAGIT/sweepSelection',
      githubPrivate: false,
      live: 'https://sweep-selection.vercel.app/',
      handOverProject: true,
      featured: false,
      details: [
        {
          image: sweepDetail1,
          description: 'Login modal interface with email and password authentication required to access the sweep giveaway system.'
        },
        {
          image: sweepDetail2,
          description: 'CSV upload and parsing interface showing successful file processing with duplicate detection and removal, displaying unique game IDs loaded into the system.'
        },
        {
          image: sweepDetail3,
          description: 'Game IDs display in a colorful gradient grid showing all unique participant IDs ready for the random sweep selection process.'
        },
        {
          image: sweepDetail4,
          description: 'Winners selection results screen displaying selected winners with confetti animation and numbered list of winning game IDs.'
        },
        {
          image: sweepDetail5,
          description: 'Download winners section with package name input field and download functionality to export selected winners as a text file.'
        }
      ]
    },
    {
      id: 3,
      title: 'City Pet Adoption Center Web Application',
      description: 'CityPet Animal Hospital is a veterinary care center dedicated to providing compassionate and professional medical services for pets. The hospital offers routine checkups, treatments, and preventive care in a clean, friendly, and pet-focused environment.',
      image: project3Image,
      technologies: ['Nest.js', 'MongoDB', 'Tailwind CSS'],
      category: 'all',
      github: 'https://github.com/samitha123456789/city-pet',
      githubPrivate: false,
      live: 'https://citypet.vercel.app/',
      featured: false
    },
    {
      id: 4,
      title: 'Receipt Printing System',
      description: 'A lightweight application designed to generate and print sales receipts efficiently for small businesses. It allows users to enter transaction details and produces structured, print-ready receipts with speed and accuracy.',
      image: codifyImage,
      technologies: ['Nest.js'],
      category: 'all',
      github: 'https://github.com/RNAGIT/codify-receipt-system',
      githubPrivate: false,
      live: 'https://codify-receipt-system.vercel.app/',
      handOverProject: true,
      featured: false,
      details: [
        {
          image: codifyDetail1,
          description: 'Create Receipt/Invoice form interface with client information and project details input fields, featuring real-time receipt preview with automatic receipt number generation.'
        },
        {
          image: codifyDetail2,
          description: 'Receipt preview panel with action buttons for printing, downloading PDF, sending email, and saving receipts, displaying formatted invoice with client billing and project information.'
        }
      ]
    },
    {
      id: 5,
      title: 'Spex ID Topup Center',
      description: 'A gaming topup center website providing instant topups for favorite games, diamond packages, and exclusive deals. Features lightning-fast delivery, secure payments, and 24/7 customer support for gamers.',
      image: spexImage,
      technologies: ['React', 'Next.js', 'MongoDB'],
      category: 'all',
      github: 'https://github.com/samitha123456789/Spex-Id-Topup-Center',
      githubPrivate: false,
      live: 'https://www.spexidtopupcenter.com/',
      featured: false
    },
    {
      id: 6,
      title: 'Simple Signin Signup',
      description: 'A simple and clean frontend application built with React and Vite for user authentication. Features intuitive signin and signup forms with modern UI design for seamless user experience.',
      image: signinSignupImage,
      technologies: ['React', 'Vite'],
      category: 'all',
      github: 'https://github.com/RNAGIT/login-cduk-test',
      githubPrivate: false,
      live: 'https://login-ebon-eight.vercel.app/',
      featured: false
    },
  ];

  const filters = [
    { key: 'all', label: 'All Projects' }
  ];

  const filteredProjects = projects.filter(project => 
    activeFilter === 'all' || project.category === activeFilter
  );

  // Check if GitHub repository is private
  const checkRepoStatus = async (githubUrl) => {
    try {
      // Extract owner and repo from URL
      const match = githubUrl.match(/github\.com\/([^/]+)\/([^/]+)/);
      if (!match) return null;
      
      const [, owner, repo] = match;
      const repoName = repo.replace(/\.git$/, ''); // Remove .git if present
      
      // Use GitHub API to check repository status
      const response = await fetch(`https://api.github.com/repos/${owner}/${repoName}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.github.v3+json'
        }
      });
      
      if (response.status === 404) {
        // Repository doesn't exist or is private
        return { exists: false, private: true };
      }
      
      if (response.ok) {
        const data = await response.json();
        return { exists: true, private: data.private };
      }
      
      return null;
    } catch (error) {
      console.error('Error checking repo status:', error);
      return null;
    }
  };

  // Handle GitHub link click
  const handleGitHubClick = async (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    
    const githubUrl = project.github;
    if (!githubUrl) return;
    
    // Check if we already know the status
    const cachedStatus = repoStatus[githubUrl];
    
    if (cachedStatus) {
      if (cachedStatus.private) {
        // Don't open, just show the private label
        return;
      }
      window.open(githubUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    
    // Check repository status
    const status = await checkRepoStatus(githubUrl);
    
    if (status) {
      // Cache the status
      setRepoStatus(prev => ({ ...prev, [githubUrl]: status }));
      
      if (status.private || !status.exists) {
        // Don't open, just show the private label
        return;
      }
    }
    
    // Open the repository
    window.open(githubUrl, '_blank', 'noopener,noreferrer');
  };

  // Check if repository is private
  const isRepoPrivate = (project) => {
    if (project.githubPrivate) return true;
    const status = repoStatus[project.github];
    return status && (status.private || !status.exists);
  };

  // Check if URL is a valid live URL
  const isLiveUrl = (url) => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://');
  };

  // Handle Live Demo link click
  const handleLiveClick = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (project.handOverProject) {
      // Mark this project as clicked
      setClickedHandoverProjects(prev => new Set(prev).add(project.id));
      return;
    }
    
    if (project.live && isLiveUrl(project.live)) {
      window.open(project.live, '_blank', 'noopener,noreferrer');
    }
  };

  // Navigate to details page
  const openDetails = (project) => {
    navigate(`/project/${project.id}/details`);
  };

  // Navigate to technologies page
  const showTechnologies = (e, project) => {
    e.preventDefault();
    e.stopPropagation();
    if (project.technologies && project.technologies.length > 0) {
      navigate(`/project/${project.id}/technologies`);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const projectVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,
      y: -10,
      transition: {
        duration: 0.3
      }
    }
  };

  return (
    <section id="projects" className="projects">
      <div className="container">
        <motion.div
          className="projects-header"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            My Projects
          </motion.h2>
          <motion.p className="section-subtitle" variants={itemVariants}>
            Some of my recent work
          </motion.p>
        </motion.div>

        <motion.div
          className="projects-filters"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {filters.map((filter) => (
            <motion.button
              key={filter.key}
              className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.key)}
              variants={itemVariants}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {filter.label}
            </motion.button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            className="projects-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            ref={ref}
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                id={`project-card-${project.id}`}
                className={`project-card ${project.featured ? 'featured' : ''}`}
                variants={projectVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                transition={{ delay: index * 0.1 }}
              >
                <div className="project-image">
                  {(project.image && typeof project.image !== 'string') || (typeof project.image === 'string' && !project.image.startsWith('/api/placeholder')) ? (
                    <motion.img 
                      src={project.image}
                      alt={`${project.title} - Project by Ransa Nilakshaya Abeywickrama (Ransa N Abeywickrama, rnabeywickrama)`}
                      className="project-img"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.6 }}
                    />
                  ) : (
                    <motion.div 
                      className="image-placeholder"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={isInView ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
                      transition={{ delay: index * 0.1, duration: 0.6 }}
                    >
                      <span>Project Image</span>
                    </motion.div>
                  )}
                </div>

                <div className="project-content">
                  <div className="project-header">
                    <h3>{project.title}</h3>
                  </div>

                  <p className="project-description">{project.description}</p>

                  <div className="project-links-bottom" onClick={(e) => e.stopPropagation()}>
                    <motion.button
                      type="button"
                      className={`project-link-icon ${isRepoPrivate(project) ? 'private-repo' : ''}`}
                      title={isRepoPrivate(project) ? "Private Repository" : "View on GitHub"}
                      onClick={(e) => handleGitHubClick(e, project)}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaGithub />
                    </motion.button>
                    {project.live && (
                      <motion.button
                        type="button"
                        className={`project-link-icon ${project.handOverProject && clickedHandoverProjects.has(project.id) ? 'handover-project' : ''}`}
                        title={project.handOverProject ? "This is a hand over project" : "View Live Demo"}
                        onClick={(e) => handleLiveClick(e, project)}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaExternalLinkAlt />
                      </motion.button>
                    )}
                    {project.details && project.details.length > 0 && (
                      <motion.button
                        type="button"
                        className="project-link-icon details-icon"
                        title="View More Details"
                        onClick={(e) => {
                          e.stopPropagation();
                          openDetails(project);
                        }}
                        whileHover={{ scale: 1.2 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FaInfoCircle />
                      </motion.button>
                    )}
                    <motion.button
                      type="button"
                      className="project-link-icon tech-icon"
                      title="View Technologies"
                      onClick={(e) => showTechnologies(e, project)}
                      whileHover={{ scale: 1.2 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <FaCode />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

      </div>
    </section>
  );
};

export default Projects;
